import React, { useState, useEffect } from "react";
import "./Personal.css";
import { useAuth } from "../../contexts/AuthContext";

const toTitleCase = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Lucide-style SVG Icons
const PersonalIcons = {
  User: () => (
    <svg
      className="w-8 h-8"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  ),
  Eye: () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  ),
  CheckCircle: () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  Calendar: () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  ),
  Loader: () => (
    <svg
      className="w-4 h-4 animate-spin"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 2v4m0 12v4m8-10h-4M6 12H2m15.364-7.364l-2.828 2.828M7.464 17.536l-2.828 2.828m12.728 0l-2.828-2.828M7.464 6.464L4.636 3.636"
      />
    </svg>
  ),
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatINR = (value = 0) => {
  const n = Number(value) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
};

const Personal = ({ userData = null, onUpdateUser }) => {
  const [profileImage] = useState(userData?.profile_image || null);
  const [creditLimit, setCreditLimit] = useState(
    userData?.credit_limit ? Number(userData.credit_limit) : 0
  );

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch real-time credit limit
  useEffect(() => {
    const fetchCreditLimit = async () => {
      if (!userData?.id) return;
      try {
        const { default: api } = await import("../../utils/apis");
        const res = await api.get(`/credit-limit/${userData.id}`);
        if (res.data?.success && res.data?.data) {
          setCreditLimit(res.data.data.total);
          // Optional: update context so it persists across navigations without refetching immediately
          if (onUpdateUser) {
            onUpdateUser({ credit_limit: res.data.data.total });
          }
        }
      } catch (error) {
        console.error("Failed to fetch real-time credit limit:", error);
      }
    };
    fetchCreditLimit();
  }, [userData?.id, onUpdateUser]);

  const personalData = {
    vendorName:
      `${userData.first_name || ""} ${userData.last_name || ""}`.trim() ||
      "Unknown Vendor",
    serviceArea: userData.work_area || "Not specified",
    email: userData.email || "Not provided",
    phoneNumber: userData.phone || "Not provided",
    creditLimitValue: creditLimit,
    accountCreatedAt: formatDate(userData.created_date),
  };

  return (
    <div className="p-4">

      {/* Page Title */}
      <div className="mb-4">
        <h1 className="md:text-2xl text-xl font-bold text-gray-900">Profile</h1>
        <p className="text-sm text-gray-500">
          View and manage administrative identity details and account limitations.
        </p>
      </div>

      {/* Main Card */}
      <div className="bg-gray-50 p-4">

        {/* Profile Header */}
        <div className="flex items-center space-x-4 pb-5 mb-5">

          <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center">
            <PersonalIcons.User />
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {toTitleCase(personalData.vendorName)}
            </h2>

            <div className="flex items-center space-x-3 mt-1">
              <span className="flex items-center text-green-600 text-xs font-medium bg-green-100 px-2 py-1 rounded-full">
                <PersonalIcons.CheckCircle />
                <span className="ml-1">Active</span>
              </span>

              <span className="text-xs text-gray-500 flex items-center">
                <PersonalIcons.Calendar />
                <span className="ml-1">
                  Member since {personalData.accountCreatedAt}
                </span>
              </span>
            </div>
          </div>

        </div>

        {/* MASTER DATA */}
        <div>
          <h3 className="text-xs font-bold tracking-widest text-teal-600 mb-4">
            MASTER DATA RECORD
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-10">

            {/* Full Name */}
            <div>
              <p className="text-xs text-gray-400 tracking-wide">FULL NAME</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {toTitleCase(personalData.vendorName)}
              </p>
            </div>

            {/* Service Area */}
            <div>
              <p className="text-xs text-gray-400 tracking-wide">SERVICE AREA</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {personalData.serviceArea}
              </p>
            </div>

            {/* Email */}
            <div>
              <p className="text-xs text-gray-400 tracking-wide">EMAIL ADDRESS</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {personalData.email}
              </p>
            </div>

            {/* Phone */}
            <div>
              <p className="text-xs text-gray-400 tracking-wide">PHONE NUMBER</p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {personalData.phoneNumber}
              </p>
            </div>

          </div>
        </div>

        {/* Credit Limit */}
        <div className="mt-6 border border-blue-200 bg-blue-50 rounded-lg p-4 flex justify-between items-center">

          <div>
            <p className="text-xs font-semibold text-blue-700 tracking-wide">
              ASSIGNED CREDIT LIMIT
            </p>
            <p className="text-xs text-blue-500">
              Maximum financial threshold allocated
            </p>
          </div>

          <div className="text-xl font-bold text-blue-700">
            {formatINR(personalData.creditLimitValue)}
          </div>

        </div>

      </div>
    </div>
  );
};

export default Personal;
